import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// ── Name matching (handles "Valdemir Silva" vs "VALDEMIR DOS SANTOS SILVA") ──
export function normalizeName(name: string): string {
  return (name ?? '').toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
}

// ── Number formatting (pt-BR) ──
export function fmtNum(v: unknown): string {
  const n = typeof v === 'string' ? parseFloat(v.replace(/[^\d.,-]/g, '').replace(',', '.')) : Number(v);
  if (isNaN(n)) return String(v ?? '');
  return n.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
}

export function fmtBRL(v: unknown): string {
  const n = typeof v === 'string' ? parseFloat(v.replace(/[^\d.,-]/g, '').replace(',', '.')) : Number(v);
  if (isNaN(n)) return `R$ ${v}`;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function fmtPct(v: unknown): string {
  const n = Number(v);
  if (isNaN(n)) return `${v}%`;
  return `${n.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
}

export function fmtBRL_K(v: number): string {
  if (v >= 1_000_000) return `R$ ${(v / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}M`;
  if (v >= 1_000) return `R$ ${(v / 1_000).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}k`;
  return fmtBRL(v);
}

// Compact BRL — used in dense KPIs and Kanban headers
export function fmtCompact(v: number): string {
  if (!isFinite(v)) return 'R$ 0';
  if (Math.abs(v) >= 1_000_000) return `R$ ${(v / 1_000_000).toFixed(2).replace('.', ',')}M`;
  if (Math.abs(v) >= 1_000) return `R$ ${(v / 1_000).toFixed(0)}K`;
  return `R$ ${v.toFixed(0)}`;
}

// "2026-04-09" → "09/04/2026"
export function fmtDate(d: string | null | undefined): string {
  if (!d) return '';
  const iso = d.length >= 10 ? d.slice(0, 10) : d;
  const [y, m, day] = iso.split('-');
  if (!y || !m || !day) return d;
  return `${day}/${m}/${y}`;
}

// "09524502000196" -> "09.524.502/0001-96" (14 digitos; senao devolve como veio)
export function fmtCnpj(raw: string | null | undefined): string {
  const d = (raw ?? '').replace(/\D/g, '');
  if (d.length !== 14) return raw ?? '';
  return d.slice(0, 2) + '.' + d.slice(2, 5) + '.' + d.slice(5, 8) + '/' + d.slice(8, 12) + '-' + d.slice(12);
}

// CPF (11) ou CNPJ (14) com dígito verificador conferido. A SEFAZ recusa NF-e
// com documento inválido (rejeição 237), então o pedido trava aqui (Thiago 28/09:
// pedido A2124 entrou com CPF de dígito errado e o cartório não consultou).
export function docValido(raw: string | null | undefined): boolean {
  const d = (raw ?? '').replace(/\D/g, '');
  if (d.length !== 11 && d.length !== 14) return false;
  if (/^(\d)\1+$/.test(d)) return false;
  const n = d.split('').map(Number);
  const dv = (pesos: number[]) => {
    const r = pesos.reduce((acc, p, i) => acc + n[i] * p, 0) % 11;
    return r < 2 ? 0 : 11 - r;
  };
  if (d.length === 11) {
    return dv([10, 9, 8, 7, 6, 5, 4, 3, 2]) === n[9]
      && dv([11, 10, 9, 8, 7, 6, 5, 4, 3, 2]) === n[10];
  }
  return dv([5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]) === n[12]
    && dv([6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]) === n[13];
}

export function daysSince(dateStr: string | null | undefined): number {
  if (!dateStr) return 0;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return 0;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

// ── Consultor filter: prefer ID match, always fallback to name ──
export function matchesConsultor(
  consultorId: string | null | undefined,
  consultorNome: string,
  recordId: string | null | undefined,
  recordNome: string | null | undefined,
): boolean {
  // If both IDs exist and match → true
  if (consultorId && recordId && consultorId === recordId) return true;
  // Always try name match as fallback (IDs may be missing or stale)
  return nameMatches(consultorNome, recordNome ?? '');
}

export function nameMatches(a: string, b: string): boolean {
  if (!a || !b) return false;
  const na = normalizeName(a);
  const nb = normalizeName(b);
  if (na === nb) return true;
  if (nb.startsWith(na) || na.startsWith(nb)) return true;
  const wa = na.split(/\s+/).filter((p) => p.length > 2);
  const wb = nb.split(/\s+/).filter((p) => p.length > 2);
  if (!wa.length || !wb.length) return false;
  const shorter = wa.length <= wb.length ? wa : wb;
  const longer = wa.length <= wb.length ? nb : na;
  return shorter.every((w) => longer.includes(w));
}

// ── Busca robusta de cliente/lead (mesma lógica do picker do Novo Pedido):
// sem acento, multi-termo em QUALQUER ordem, casando por nome/cidade/uf/produto,
// ou por CNPJ (>=3 dígitos). Evita o "não achei o cliente → criei duplicata"
// (caso agenda avulsa 25/05). Busca vazia = casa tudo (lista inicial).
export function buscaMatch(
  alvo: { nome?: string | null; cidade?: string | null; uf?: string | null; cnpj?: string | null; produto?: string | null },
  busca: string,
): boolean {
  const q = (busca ?? '').trim();
  if (!q) return true;
  const norm = (s: string) => (s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const digits = q.replace(/\D/g, '');
  if (digits.length >= 3 && (alvo.cnpj ?? '').replace(/\D/g, '').includes(digits)) return true;
  const terms = norm(q).split(/\s+/).filter(Boolean);
  const hay = norm(`${alvo.nome ?? ''} ${alvo.cidade ?? ''} ${alvo.uf ?? ''} ${alvo.produto ?? ''}`);
  return terms.every((t) => hay.includes(t));
}
